<script setup lang="ts">
import { app } from '@/composition/app'
import { useCommunitiesToastNotification } from '@/helper/communitiesToastNotification.helper'
import { Platform } from '@/helper/const'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { getEncodedUrl } from '@/util/helper'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { SafeScreenOrientation } from '@ghl/clientportal-core'
import { isPwa } from '@ghl/clientportal-core/init'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Download01Icon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UICard } from '@gohighlevel/ghl-ui'
import { NModal } from 'naive-ui'
import { computed, defineAsyncComponent, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { downloadFile } from '../../util/fileHandler'
import { getOriginalName } from '../helper'
import CommunityButton from './buttons/CommunityButton.vue'

const VideoPlayer = defineAsyncComponent(() => import('./VideoPlayer.vue'))

const props = defineProps({
  showModal: Boolean,
  file: Object,
  currentIndex: {
    type: Number,
    default: 0,
  },
  totalAttachments: Number,
})
const route = useRoute()
const notification = useCommunitiesToastNotification()
const { t } = useI18n()

const handlePopState = () => {
  console.log('Emiting close')
  emit('update:close')
}

watch(
  () => props.showModal,
  value => {
    if (value) {
      window.addEventListener('popstate', handlePopState)
      // Push state when modal opens for both web and mobile
      window.history.pushState(
        { type: 'attachment-preview' },
        '',
        window.location.href
      )

      // Mobile-specific viewport handling
      if (app.state.isMobile) {
        if (props.file?.type.includes('image')) {
          document
            .getElementsByName('viewport')[0]
            .setAttribute(
              'content',
              'initial-scale=1.0,maximum-scale=10.0,user-scalable=yes'
            )
        } else {
          document
            .getElementsByName('viewport')[0]
            .setAttribute(
              'content',
              'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
            )
        }
      }

      // Screen orientation handling for native platforms
      SafeScreenOrientation.unlock()
    }
  }
)

const emit = defineEmits(['update:close', 'update:show'])

function closeModal() {
  // Reset viewport dynamically
  const viewportMeta = document.querySelector('meta[name="viewport"]')
  if (viewportMeta && isPwa()) {
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
    )
  }

  // Explicitly reset CSS scale (reliable method)
  document.body.style.transform = 'scale(1)'
  document.body.style.transformOrigin = '0 0'

  setTimeout(() => {
    document.body.style.transform = ''
  }, 100)
  emit('update:close')
}

const showLeftBtn = computed(() => props.currentIndex !== 0)
const showRightBtn = computed(() => {
  if (props.totalAttachments) {
    return props.currentIndex !== props.totalAttachments - 1
  }
  return false
})

async function download() {
  await downloadFile(props?.file?.url, props?.file?.name)
}

function changePreview(index: number) {
  emit('update:show', index)
}

/**
 *
 * We need to fetch the PDF before we directly set anchor tag href to the file URL.
 * Because, HTML only allows same origin resources to be downloadable directly via download attribute.
 *
 */
function downloadPdf() {
  fetch(props.file?.url)
    .then(response => {
      return response.blob()
    })
    .then(async blob => {
      if (
        Capacitor.getPlatform() === Platform.IOS ||
        Capacitor.getPlatform() === Platform.Android
      ) {
        await Browser.open({ url: props.file?.url })
      } else {
        // Create a blob URL for the PDF data
        const url = window.URL.createObjectURL(blob)

        // Create a link element to trigger the download
        const a = document.createElement('a')
        a.href = url
        a.download = getOriginalName(props.file?.name) || 'file'
        document.body.appendChild(a)

        // Trigger a click event on the link element to initiate the download
        a.click()

        // Clean up by revoking the blob URL and removing the link element
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    })
    .catch(error => {
      console.error('Download error:', error)
      notification.create({
        title: `${t('common.failedToDownloadPdf')}: ${error.message}`,
        type: 'error',
        duration: 3000,
      })
    })
}

onUnmounted(() => {
  // Clean up event listener
  window.removeEventListener('popstate', handlePopState)

  // Reset screen orientation for native platforms
  SafeScreenOrientation.lock({ orientation: 'portrait' })
})
</script>
<template>
  <NModal
    :show="showModal"
    :mask-closable="false"
    :block-scroll="false"
    inputmode="none"
    :auto-focus="false"
    :z-index="2999"
  >
    <div
      class="fixed inset-0 z-50 h-full w-full select-none overflow-hidden"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="relative flex h-screen items-center justify-center px-4 text-center sm:block sm:p-0"
      >
        <!-- hard coding background and font color because it should not be changed by any theme -->
        <div
          class="absolute inset-0 left-0 top-0 h-full w-full bg-[#18191AFF] opacity-95 transition-opacity"
          aria-hidden="true"
        />
        <span
          class="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
          >&#8203;</span
        >
        <div class="relative inline-block h-full w-full bg-opacity-50">
          <div
            v-if="file"
            class="absolute left-1/2 top-0 z-50 mx-auto mb-4 mt-6 flex w-11/12 -translate-x-1/2 items-center justify-between text-[#FFFFFF]"
            :class="!route.params.postId && !app.state.isMobile ? 'mt-12' : ''"
          >
            <div class="break-all">
              {{ getOriginalName(file.name) || file.title || 'File' }}
            </div>
            <div class="flex flex-row items-center gap-3 pl-2">
              <div
                v-if="
                  !file.type.includes('video') && !file.type.includes('image')
                "
                tabindex="0"
                role="button"
                aria-label="Download File"
                @click="downloadPdf"
                @keyup.enter="downloadPdf"
              >
                <Download01Icon class="h-6 w-6 cursor-pointer" />
              </div>
              <div
                tabindex="0"
                role="button"
                aria-label="Close Modal"
                @click="closeModal"
                @keyup.enter="closeModal"
              >
                <XCloseIcon class="h-6 w-6 cursor-pointer" />
              </div>
            </div>
          </div>
          <div class="relative mt-5 h-full">
            <div
              v-if="file"
              class="ui-modal absolute top-1/2 w-full -translate-y-1/2 transform overflow-hidden"
            >
              <div
                v-if="file.provider_name"
                class="embed-video-preview mx-auto"
              >
                <div v-sanitized-html="file.html" class="h-full w-full" />
              </div>
              <div
                v-else-if="file.type.includes('video')"
                :key="file.name"
                class="video-preview mx-auto"
              >
                <VideoPlayer
                  :src="`${getEncodedUrl(file.url)}#t=0.001`"
                  :poster="getEncodedUrl(file.thumbnailUrl) || ''"
                />
              </div>
              <div
                v-else-if="file.type.includes('image')"
                :key="file.name + ' image'"
                class="h-auto w-full"
              >
                <img
                  ref="imageAttachment"
                  :src="replaceBucketUrlWithCdnUrl(file.url)"
                  class="max-h-[80vh] rounded-xl object-contain"
                  style="max-width: 100%; width: auto; height: auto"
                  alt="Attachment"
                />
              </div>
              <div
                v-else-if="file.type.includes('pdf')"
                :key="file.name + ' pdf'"
                class="mx-auto"
                style="width: 100%; height: 90vh"
              >
                <object
                  width="100%"
                  height="100%"
                  :data="`${file.url}`"
                  type="application/pdf"
                >
                  <div class="pt-10 text-white">
                    {{ t('common.unableToPreviewPdf') }}
                  </div>
                </object>
              </div>
              <div v-else class="mx-auto w-2/12 py-2 sm:p-6 sm:pb-4">
                <UICard>
                  <div class="pb-2 pt-4 text-xl">
                    {{ t('common.noPreviewAvailable') }}
                  </div>
                  <div class="pb-6">for {{ file.name }}</div>
                  <CommunityButton
                    id="download-file__btn"
                    button-type="secondary"
                    @click="download"
                  >
                    {{ t('common.download') }}
                  </CommunityButton>
                </UICard>
              </div>
            </div>
            <div
              v-if="showLeftBtn"
              class="w-30 absolute left-4 top-1/2 flex -translate-y-1/2 transform cursor-pointer items-center rounded-full bg-communities-background p-2 text-communities-font-primary"
              tabindex="0"
              role="button"
              aria-label="Previous Attachment"
              @click="changePreview(currentIndex - 1)"
              @keyup.enter="changePreview(currentIndex - 1)"
            >
              <ChevronLeftIcon class="h-6 w-6" />
            </div>
            <div
              v-if="showRightBtn"
              class="w-30 absolute right-4 top-1/2 flex -translate-y-1/2 transform cursor-pointer items-center rounded-full bg-communities-background p-2 text-communities-font-primary"
              tabindex="0"
              role="button"
              aria-label="Next Attachment"
              @click="changePreview(currentIndex + 1)"
              @keyup.enter="changePreview(currentIndex + 1)"
            >
              <ChevronRightIcon class="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </NModal>
</template>
<style scoped>
.video-preview {
  max-width: 1180px;
  max-height: 720px;
}
.embed-video-preview {
  max-width: 1180px;
  max-height: 720px;
  width: 93vw;
  height: 650px;
}
@media (max-width: 930px) {
  .embed-video-preview {
    max-width: 1180px;
    max-height: 720px;
    width: 93vw;
    height: 300px;
  }
}
</style>
<style>
iframe {
  width: 100%;
  height: 100%;
}
</style>
